import React, { useCallback, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Spinner,
} from '@material-tailwind/react'
import { PiWarningCircle } from 'react-icons/pi'
import { useGeneralToast } from '../hooks/toast'
import { useWindowSize } from '../hooks/toolkit'
import { deleteUserPattern } from '../utils/helpers/supabase.helpers'
import { AgamiPattern } from '../types/supabase'

interface DeletePatternDialogProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  pattern: AgamiPattern
  refetch: () => void
}

const DeletePatternDialog: React.FC<DeletePatternDialogProps> = ({ isOpen, setIsOpen, pattern, refetch }) => {
  const { success: toastSuccess, error: toastError } = useGeneralToast()
  const [deleting, setDeleting] = useState(false)
  const { width } = useWindowSize()
  const dialogSize = (width ?? 0) <= 640 ? 'lg' : 'xs'

  const deletePattern = useCallback(async () => {
    setDeleting(true)

    const { error } = await deleteUserPattern({ patternId: pattern.pattern_id })

    if (error) {
      toastError('Something went wrong :(')
      setDeleting(false)
      return
    }

    await refetch()
    toastSuccess('Pattern sucessfully deleted')
    setDeleting(false)
    setIsOpen(false)
  }, [pattern])

  return (
    <Dialog size={dialogSize} open={isOpen} handler={setIsOpen} data-testid="pattern-delete-dialog">
      <DialogHeader>Delete pattern</DialogHeader>

      <DialogBody>
        <Alert variant="ghost" color="orange" icon={<PiWarningCircle className="h-6 w-6" />}>
          This operation cannot be undone!
        </Alert>
        <span className="block my-2">
          Are you sure you want to delete the pattern <strong>{pattern.name}</strong>?
        </span>
      </DialogBody>

      <DialogFooter className="justify-between gap-2">
        <Button size="sm" variant="text" onClick={() => setIsOpen(false)} disabled={deleting}>
          CANCEL
        </Button>
        <Button
          size="sm"
          color="red"
          className="bg-accent-red"
          onClick={deletePattern}
          disabled={deleting}
          data-testid="pattern-delete-confirm-btn"
        >
          {deleting ? <Spinner className="h-4 w-4" /> : 'CONFIRM DELETE'}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default DeletePatternDialog
